import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { Clock, AlertTriangle, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';

/**
 * OPARA v7.4 - Trial Banner Component
 * Displays trial status information for free users
 * - Shows remaining trial days during active trial
 * - Shows "trial expired" message with purchase button when trial ends
 */
const TrialBanner = ({ user, locale: propLocale }) => {
  const navigate = useNavigate();
  const { t, locale: hookLocale } = useTranslation();
  
  // Use prop locale if provided, otherwise use hook locale
  const locale = propLocale || hookLocale;
  
  // Only show for free plan users
  if (!user || user.plan_type !== 'free') {
    return null;
  }
  
  const trialActive = user.trial_active;
  const trialDaysRemaining = user.trial_days_remaining;
  
  // Get locale-aware pricing path
  const getPricingPath = () => {
    const paths = {
      en: '/en/pricing',
      tr: '/tr/kredi-paketleri',
      ar: '/ar/pricing',
      de: '/de/pricing',
      fr: '/fr/pricing',
      es: '/es/pricing'
    };
    return paths[locale] || paths.en;
  };
  
  // Trial expired - show purchase prompt.
  // GUVENLI-VARSAYILAN: yalniz trialActive === false ise "bitti" goster. undefined/null (durum
  //   bilinmiyor) ise HICBIR SEY render etme -> yanlis "Trial Ended" gostermek, hic gostermemekten
  //   daha zararli (promosyon banner'i).
  if (trialActive === false) {
    return (
      <div 
        className="w-full bg-gradient-to-r from-red-900/90 to-red-800/90 border border-red-500/30 rounded-lg p-4 mb-4"
        data-testid="trial-expired-banner"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div>
              <h3 className="text-white font-semibold text-sm">
                {t('trial.expired_title')}
              </h3>
              <p className="text-red-200 text-xs mt-0.5">
                {t('trial.expired_message')}
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate(getPricingPath())}
            className="bg-white text-red-600 hover:bg-red-50 font-semibold px-4 py-2 text-sm"
            data-testid="trial-purchase-btn"
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            {t('trial.purchase_btn')}
          </Button>
        </div>
      </div>
    );
  }
  
  // Trial active - show remaining days
  if (trialActive && trialDaysRemaining !== null && trialDaysRemaining !== undefined) {
    // Determine urgency level
    const isLastDay = trialDaysRemaining <= 1;
    const isUrgent = trialDaysRemaining <= 3;
    
    // Dynamic styling based on urgency
    const bgClass = isLastDay 
      ? 'from-orange-900/90 to-orange-800/90 border-orange-500/30'
      : isUrgent 
        ? 'from-yellow-900/90 to-yellow-800/90 border-yellow-500/30'
        : 'from-blue-900/90 to-blue-800/90 border-blue-500/30';
    
    const iconColor = isLastDay 
      ? 'text-orange-400' 
      : isUrgent 
        ? 'text-yellow-400' 
        : 'text-blue-400';
    
    const textColor = isLastDay 
      ? 'text-orange-200' 
      : isUrgent 
        ? 'text-yellow-200' 
        : 'text-blue-200';
    
    // Get appropriate message
    let daysMessage;
    if (isLastDay && trialDaysRemaining === 0) {
      daysMessage = t('trial.last_day');
    } else if (trialDaysRemaining === 1) {
      daysMessage = t('trial.day_remaining');
    } else {
      daysMessage = t('trial.days_remaining', { days: trialDaysRemaining });
    }
    
    return (
      <div 
        className={`w-full bg-gradient-to-r ${bgClass} rounded-lg p-3 mb-4`}
        data-testid="trial-active-banner"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Clock className={`h-4 w-4 ${iconColor} flex-shrink-0`} />
            <span className={`${textColor} text-sm font-medium`}>
              {daysMessage}
            </span>
            <span className={`${textColor} text-xs opacity-75 hidden sm:inline`}>
              • {t('trial.daily_credit_info')}
            </span>
          </div>
          {isUrgent && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(getPricingPath())}
              className="border-white/30 text-white hover:bg-white/10 text-xs px-3 py-1"
              data-testid="trial-upgrade-btn"
            >
              {t('trial.purchase_btn')}
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return null;
};

export default TrialBanner;
